<script setup lang="ts">
import {computed, reactive, ref, useTemplateRef, watch} from "vue";
import {RouterLink, useRoute, useRouter} from "vue-router";
import {useConfirm} from "primevue/useconfirm";
import {useToast} from "primevue/usetoast";
import {deleteGroup, getGroup, toggleLinearFlow, togglePostingRule} from "@/Services/groups.ts";
import {massChangeGroupMemberRole, removeGroupMember} from "@/Services/group-members.ts";
import {useMainStore} from "@/Stores/store.ts";
import useEcho from "@/Composables/echo.ts";
import GroupEvents from "../group-events.ts";
import User from "@/Models/User.ts";
import {Avatar, Button, ImportantInfoPanel, Popover, ToggleSwitch} from "@/Components/Library";
import {PageLoading, PageTitle} from "@/Components/Global";
import AnalyzeGroupDrawer from "../AnalyzeGroupDrawer.vue";
import {GroupRole} from "@/Enums";
import PageContainer from "@/Components/Global/PageContainer.vue";
import GroupMembers from "@/Pages/User/Groups/Group/GroupMembers.vue";
import GroupFeed from "@/Pages/User/Groups/Group/GroupFeed.vue";
import {deleteConversation} from "@/Services/conversations.ts";
import {restoreGroupConversation} from "@/Services/group-conversations.ts";
import CloneGroupDialog from "@/Pages/User/Groups/CloneGroupDialog.vue";

const $store = useMainStore();
const $user = new User();
const confirm = useConfirm();
const toast = useToast();
const router = useRouter();
const currentRoute = useRoute();

const state = reactive({
    show: {
        analysisModal: false,
        cloneGroupDialog: false,
    },
    loading: {
        group: false,
    },
});
const analysisParams = ref<{
    filter: Record<string, unknown>;
    sort: string[];
} | null>(null);
// Fetching
const group = computed({get: () => $store.group, set: (val) => ($store.group = val)});

const groupConversationToggle = computed({
    get: () => !!group.value?.conversation,
    set: (newValue: boolean) => toggleGroupConversation(newValue),
});
const allowMembersPostToggle = computed({
    get: () => !!group.value?.params?.allow_member_posts,
    set: (newValue) => toggleMembersPosting(newValue),
});
const linearFlowToggle = computed({
    get: () => !!group.value?.params?.linear_flow,
    set: (newValue) => toggleLinearFlowSetting(newValue),
});
// const canToggleMassRole = computed(() => {
//     if (!group.value?.members?.length) {
//         return false;
//     }
//
//     return group.value.members.some(
//         (m) => m.pivot?.role === GroupRole.member.value || m.pivot?.role === GroupRole.creator.value,
//     );
// });
const everybodysSoCreative = computed({
    get() {
        if (!group.value?.members?.length) {
            return false;
        }
        return !group.value.members
            .filter((m) => m.pivot?.role !== GroupRole.admin.value)
            .some((m) => m.pivot?.role === GroupRole.member.value)
            ? "creator"
            : "member";
    },
    set(val: "creator" | "member") {
        toggleRole(val);
    },
});
const loadGroup = async () => {
    state.loading.group = true;
    group.value = {questions: [], members: []};
    getGroup((currentRoute.query.group as string) ?? currentRoute.params.group)
        .then(({data}) => {
            Object.assign($store.group, data);
            // questions.value = data.questions;
            if (!group.value?.members?.length) {
                state.show.pendingInvitations = true;
            }
        })
        .finally(() => (state.loading.group = false));
};
if (((currentRoute.query.group as string) ?? currentRoute.params.group) !== group.value?.id) {
    loadGroup();
}

watch(
    () => (currentRoute.query.group as string) ?? (currentRoute.params.group as string),
    (newId, oldId) => {
        if (newId && newId !== group.value?.id) {
            loadGroup();
        }
    },
);

const groupPopoverContainer = useTemplateRef("groupPopoverContainer");
const showGroupPopover = (e) => {
    groupPopoverContainer?.value?.toggle(e);
};

const showCloneGroupDialog = () => {
    groupPopoverContainer.value?.hide();
    state.show.cloneGroupDialog = true;
};

const leaveGroup = () => {
    confirm.require({
        header: "Leave group",
        message: "This will cancel your membership and revoke access to the group",
        acceptClass: "bg-red-600 px-3 py-1.5 rounded-md text-white",
        rejectClass: "px-3 py-1.5",
        accept: async () => {
            await removeGroupMember(group.value.id, $user.id).then(() => {
                group.value.members = group.value.members.filter((member) => member.id !== $user.id);
                toast.add({
                    severity: "success",
                    summary: "Success",
                    detail: "You have left the group.",
                    life: 3000,
                });
                router.push({name: "groups.index"});
            });
        },
    });
};

const toggleGroupConversation = (enable: boolean = false) => {
    confirm.require({
        header: `${enable ? "Enable" : "Disable"} group chat`,
        message: `Are you sure you want to ${enable ? "enable" : "disable"} the group chat?`,
        acceptClass: "bg-red-600 px-3 py-1.5 rounded-md text-white",
        rejectClass: "px-3 py-1.5",
        accept: async () => {
            if (enable) {
                const {data} = await restoreGroupConversation(group.value.id);
                group.value.conversation = data;
            } else {
                await deleteConversation(group.value.conversation.id);
                group.value.conversation = null;
            }
            toast.add({
                severity: "success",
                summary: "Success",
                detail: `Group chat has been ${enable ? "enabled" : "disabled"}.`,
                life: 3000,
            });
        },
    });
};

const toggleMembersPosting = (allow: boolean = false) => {
    let title = allow ? "Allow members to post" : "Disable members from posting";
    let message = allow
        ? "Are you sure you want to allow all members to post in this group?"
        : "Are you sure you want to prevent members from posting in this group?";
    let successMessage = allow ? "Members can now post in this group." : "Members can no longer post in this group.";
    confirm.require({
        header: title,
        message: message,
        acceptClass: "bg-red-600 px-3 py-1.5 rounded-md text-white",
        rejectClass: "px-3 py-1.5",
        accept: async () => {
            await togglePostingRule(group.value.id, allow);
            group.value.params.allow_member_posts = allow;
            if (!allow) {
                group.value.members.forEach((member) => {
                    if (member.pivot.role === GroupRole.creator.value) {
                        member.pivot.role = GroupRole.member.value;
                    }
                    return member;
                });
            }
            toast.add({
                severity: "success",
                summary: "Success",
                detail: successMessage,
                life: 3000,
            });
        },
    });
};

const toggleLinearFlowSetting = (enable: boolean = false) => {
    confirm.require({
        header: `${enable ? "Enable" : "Disable"} linear flow`,
        message: `Are you sure you want to ${enable ? "enable" : "disable"} the linear flow?`,
        acceptClass: "bg-red-600 px-3 py-1.5 rounded-md text-white",
        rejectClass: "px-3 py-1.5",
        accept: async () => {
            try {
                await toggleLinearFlow(group.value.id, enable);
                group.value.params.linear_flow = enable;
                toast.add({
                    severity: "success",
                    summary: "Success",
                    detail: enable
                        ? "Members must answer the questions in order."
                        : "Members can answer the questions in any order.",
                    life: 3000,
                });
            } catch (error) {
                console.error(error);
                toast.add({
                    severity: "error",
                    summary: "Error",
                    detail: "Failed to update the linear flow setting.",
                    life: 3000,
                });
            }
        },
    });
};

const confirmDeleteGroup = () => {
    confirm.require({
        header: `Archive "${group.value.name}"`,
        message: "Are you sure you want to archive this group?",
        acceptLabel: "Yes",
        rejectLabel: "No",
        acceptClass: "px-3 py-1.5 rounded-md confirm-decline-yes text-white font-bold bg-red-600 focus:ring-red-400",
        rejectClass: "px-3 py-1.5 font-bold confirm-decline-no",
        accept: async () => {
            await deleteGroup(group.value.id);
            toast.add({
                severity: "success",
                summary: "Success",
                detail: "You have archived the group.",
                life: 3000,
            });
            router.push({name: "groups.index"});
        },
    });
};

const showAnalysis = (params: {filter: Record<string, unknown>; sort: string[]}) => {
    analysisParams.value = params;
    state.show.analysisModal = true;
};

const toggleRole = (newRole: "creator" | "member") => {
    const oldRole = newRole === "creator" ? "member" : "creator";

    confirm.require({
        header: `Change Member Roles`,
        message: `Are you sure you want to change all ${oldRole}s to ${newRole}s? This will not affect admins or the group owner.`,
        accept: async () => {
            try {
                await massChangeGroupMemberRole(group.value.id, {role: newRole});
                group.value.members = group.value.members.map((member) => {
                    if (member.pivot.role === GroupRole[oldRole].value) {
                        member.pivot.role = GroupRole[newRole].value;
                    }
                    return member;
                });
                if (newRole === "creator") {
                    group.value.params.allow_member_posts = true;
                }
                toast.add({
                    severity: "success",
                    summary: "Roles Updated",
                    detail: `All group ${oldRole}s are now ${newRole}s.`,
                    life: 3000,
                });
            } catch (error) {
                console.error("Failed to update roles:", error);
            }
        },
        reject: () => {},
        acceptClass: "bg-askr-green text-white px-3 py-1.5 rounded-md",
        rejectClass: "px-3 py-1.5",
    });
};

useEcho(GroupEvents(group, {}));
</script>
<template>
    <PageContainer class="">
        <div class="flex">
            <slot>
                <PageTitle title="Group" :route="{name: 'feed', params: {group: group?.id}}" />
            </slot>
            <Button
                :icon="{
                    icon: group.can_admin ? 'prime:cog' : 'pepicons-pencil:dots-y',
                    width: '24',
                }"
                class="ml-auto !ring-0"
                @click.stop="showGroupPopover"
            />
        </div>
        <Popover ref="groupPopoverContainer" pt:content:class="p-0">
            <div class="flex w-full max-w-xl flex-col gap-1">
                <div v-if="group.can_admin" class="flex flex-col gap-2 px-4 py-2">
                    <div class="flex items-center justify-between gap-2">
                        <span>Enable group chat</span>
                        <ToggleSwitch v-model="groupConversationToggle" input-id="group-chat-toggle" />
                    </div>
                    <div class="flex items-center justify-between gap-2">
                        <span>Allow members to post</span>
                        <ToggleSwitch v-model="allowMembersPostToggle" input-id="allow-post-toggle" />
                    </div>
                    <div class="flex items-center justify-between gap-2">
                        <span>Everybody is a creator</span>
                        <ToggleSwitch
                            v-model="everybodysSoCreative"
                            true-value="creator"
                            false-value="member"
                            input-id="all-creators-toggle"
                        />
                    </div>
                    <div class="flex items-center justify-between gap-2">
                        <span>Linear flow</span>
                        <ToggleSwitch v-model="linearFlowToggle" input-id="linear-flow-toggle" />
                    </div>
                </div>
                <Button
                    v-if="group?.is_owner || group?.can_admin"
                    :icon="{icon: 'prime:pencil'}"
                    class="!justify-start rounded-md hover:bg-stone-200 dark:hover:bg-stone-700"
                    @click="router.push({name: 'groups.edit', params: {group: group.id}})"
                >
                    Edit
                </Button>
                <Button
                    v-if="group.is_owner"
                    :icon="{icon: 'prime:copy'}"
                    class="!justify-start rounded-md hover:bg-stone-200 dark:hover:bg-stone-700"
                    @click="showCloneGroupDialog"
                >
                    Clone group
                </Button>
                <Button
                    v-if="group.is_owner"
                    :icon="{icon: 'prime:trash'}"
                    class="!justify-start rounded-md text-red-600 hover:bg-stone-200 dark:hover:bg-stone-700"
                    @click="confirmDeleteGroup"
                >
                    Archive
                </Button>
                <Button
                    v-else
                    :icon="{icon: 'prime:user-minus'}"
                    class="!justify-start rounded-md text-red-600 hover:bg-stone-200 dark:hover:bg-stone-700"
                    @click="leaveGroup"
                >
                    Leave Group
                </Button>
            </div>
        </Popover>
        <CloneGroupDialog v-if="group?.id" v-model:visible="state.show.cloneGroupDialog" :group="group" />
        <PageLoading v-if="state.loading.group" class="pt-16" />
        <div v-if="group?.id">
            <div class="gap-3 xl:flex">
                <div class="xl:h--full mb-4 xl:mb-12 xl:w-full xl:max-w-lg">
                    <div class="sticky top-0 overflow-auto pr-3 pt-3">
                        <div class="mb-2 flex flex-col items-center xl:items-start">
                            <div class="mb-4 flex flex-col items-center gap-4 xl:flex-row">
                                <Avatar
                                    class="h-12 w-12 text-xl"
                                    :class="[
                                        `bg-${group.color?.class}`,
                                        group.color?.class === 'white' ? 'text-black' : 'text-white',
                                    ]"
                                    :initials="group.initials"
                                    :image="group.icon?.url"
                                />
                                <h2 class="text-lg font-bold sm:text-2xl xl:m-0">
                                    {{ group.name }}
                                </h2>
                            </div>
                            <!--                <TagList :tags="group.tags" class="mb-2" center />-->
                            <p class="mb-6 max-w-3xl text-center xl:text-left">{{ group.description }}</p>
                            <ImportantInfoPanel
                                v-if="group.important_info"
                                :content="group.important_info"
                                class="mb-6 max-w-3xl"
                            />
                            <RouterLink
                                v-if="group.conversation?.id"
                                :to="{name: 'conversations.show', params: {conversation: group.conversation.id}}"
                                class="mb-4 block flex w-full justify-center"
                            >
                                <Button class="w-full bg-askr-green text-white" icon="prime:comment">
                                    View group chat
                                </Button>
                            </RouterLink>
                        </div>
                        <!-- Members list -->
                        <div v-if="group.anonymization.slug !== 'invisible' || group.is_owner" class="mb-3">
                            <GroupMembers :group="group" />
                        </div>
                    </div>
                </div>
                <!-- Questions list -->
                <div class="max-w--7xl grow pt-3">
                    <GroupFeed :group="group" @analyze="showAnalysis" />
                </div>
            </div>
        </div>
        <AnalyzeGroupDrawer
            v-if="group?.id"
            v-model:visible="state.show.analysisModal"
            :group="group"
            :feed-params="analysisParams"
        />
    </PageContainer>
</template>
<style scoped></style>
